import React, {FC, useMemo} from 'react';

import {defineMessage} from '../../../../../libs/shared/helpers/src/internationalization/message';
import {useLocalize} from '../../../../../srcCommon/internationalization/internationalizationContext';
import {useAppDispatch} from '../../../../helpers/reduxHelpers';
import {AnalyticsTableMetricsEnum} from '../../../../models/api/analyticsMetricsModel';
import {setMetricDetailsAction} from '../../../../store/panels/analytics/details/analyticsPanelDetailsActions';
import {
  BasicReportProps,
  ContributingSegmentsOptionsEnum,
  ReportElementIdsEnum,
  ReportFilterOptionsEnum,
} from '../../../helpers/analyticsReportHelpers';
import {getMetricLabels} from '../../../helpers/metricLabels';
import {AnalyticsComputedGraphMetricsEnum} from '../../../transformers/analyticsMetricsTransformersTypes';
import {
  getWhatHappenedSeries,
  getWhatHappenedSubmetricLabel,
  WhatHappenedChartSeries,
  WhatHappenedSerieTypesEnum,
} from '../../../transformers/whatHappenedTableToGraph';
import {ChartSerie} from '../../charts/commons/types';
import {ReportLineChart} from '../../reportElements/charts/reportLineChart';
import {ReportSankeyChart} from '../../reportElements/charts/reportSankeyChart';
import {ReportTable} from '../../reportElements/table/reportTable';
import {teamColumnId} from '../../reportElements/table/tableHelpers';

/*
 * Props.
 */

interface ConversationsReportProps extends BasicReportProps {
  isTeamColumnVisible: boolean;
}

/*
 * Component.
 */

export const ConversationsReport: FC<ConversationsReportProps> = ({isLoading, isTeamColumnVisible}) => {
  const dispatch = useAppDispatch();
  const localize = useLocalize();

  // @TODO: implement this in the metric parser.
  const defaultColumnIds = [
    'open_at_start',
    'moved_into_inbox',
    'new_direct_in_inbox',
    'reopened',
    'archived_in_inbox',
    'moved_out_of_inbox',
    'open_at_end',
  ];

  const whatHappenedSeries = useMemo(() => getWhatHappenedSeries(localize), [localize]);

  const onDataPointSelect = (
    dataPoint: WhatHappenedChartSeries,
    serie: ChartSerie<WhatHappenedChartSeries>,
  ) => {
    dispatch(
      setMetricDetailsAction(
        AnalyticsTableMetricsEnum.WHAT_HAPPENED_TABLE,
        {
          [ReportFilterOptionsEnum.COLLECT_CONTRIBUTING_SEGMENT_IDS]: {
            [ContributingSegmentsOptionsEnum.ROW]: dataPoint.bucketKey,
            [ContributingSegmentsOptionsEnum.SUB_METRIC]: serie.key,
          },
        },
        getWhatHappenedSubmetricLabel(localize, serie.key as WhatHappenedSerieTypesEnum),
      ),
    );
  };

  return (
    <>
      {/* What happened */}
      <ReportLineChart
        reportSectionId={ReportElementIdsEnum.CONVERSATIONS_WHAT_HAPPENED}
        title={localize(
          defineMessage({
            id: 'conversations-report-what-happened-title',
            message: 'What Happened',
          }),
        )}
        isLoading={isLoading}
        availableMetrics={[AnalyticsComputedGraphMetricsEnum.WHAT_HAPPENED_GRAPH]}
        series={whatHappenedSeries}
        xAccessor={(d: WhatHappenedChartSeries) => d.label}
        onDataPointSelect={onDataPointSelect}
        disablePreviousToggle
      />

      {/* Conversation flows */}
      <ReportSankeyChart
        reportSectionId={ReportElementIdsEnum.CONVERSATIONS_SANKEY}
        title={localize(
          defineMessage({
            id: 'conversations-report-flows-title',
            message: 'Conversation Flows',
          }),
        )}
        infoTooltip={getMetricLabels(AnalyticsComputedGraphMetricsEnum.SANKEY_GRAPH).tooltip}
        isLoading={isLoading}
        metric={AnalyticsComputedGraphMetricsEnum.SANKEY_GRAPH}
      />

      {/* Inboxes */}
      <ReportTable
        reportSectionId={ReportElementIdsEnum.CONVERSATIONS_INBOXES}
        isLoading={isLoading}
        metric={AnalyticsTableMetricsEnum.INBOX_TABLE}
        defaultColumnIds={defaultColumnIds}
        disableSelection
        blockedColumnIds={isTeamColumnVisible ? undefined : [teamColumnId]}
        initialState={{
          sortBy: [
            {
              id: 'open_at_start',
              desc: true,
            },
          ],
        }}
      />
    </>
  );
};
